import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, Settings2, X } from "lucide-react";
import { getConsent, saveConsent, acceptAll, rejectNonEssential } from "@/lib/consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false, preferences: true });

  useEffect(() => {
    if (!getConsent()) setVisible(true);
  }, []);

  const onAcceptAll = () => { acceptAll(); setVisible(false); };
  const onRejectNon = () => { rejectNonEssential(); setVisible(false); };
  const onSave = () => { saveConsent(prefs); setVisible(false); setShowPrefs(false); };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] animate-fade-up" data-testid="cookie-banner">
        <div className="glass rounded-2xl shadow-[0_20px_60px_-15px_rgba(11,18,32,0.35)] p-5">
          <div className="flex items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#1565FF]/10 text-[#1565FF] dark:bg-white/5 dark:text-[#4FC3F7]">
              <Cookie className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold tracking-tight">We value your privacy</div>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                We use cookies to make the site work, measure performance, and improve your experience.
                Read our{" "}
                <Link to="/privacy" className="text-[#1565FF] underline">Privacy Policy</Link> &{" "}
                <Link to="/cookies" className="text-[#1565FF] underline">Cookie Policy</Link>.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={onAcceptAll} className="btn-primary !py-2 !px-4 !text-xs" data-testid="cookie-accept-all">Accept all</button>
                <button onClick={onRejectNon} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="cookie-reject-non-essential">Reject non-essential</button>
                <button onClick={() => setShowPrefs(true)} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="cookie-customize">Customize</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPrefs && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="cookie-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-[#0d1a35] border border-black/5 dark:border-white/10 shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-black/5 dark:border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold tracking-tight">Cookie preferences</h3>
                <p className="text-xs text-muted-foreground mt-1">Choose which categories of cookies we can use.</p>
              </div>
              <button onClick={() => setShowPrefs(false)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5" data-testid="cookie-modal-close"><X className="h-4 w-4"/></button>
            </div>
            <div className="p-6 space-y-3">
              <ConsentRow icon={Shield} title="Strictly Necessary" description="Required for login, security, and core functionality. Always active." checked disabled />
              <ConsentRow icon={BarChart3} title="Analytics" description="Google Analytics helps us understand how the site is used." checked={prefs.analytics} onChange={(v) => setPrefs({...prefs, analytics: v})} testId="cookie-toggle-analytics" />
              <ConsentRow icon={Megaphone} title="Marketing" description="Marketing tools help us reach the right audience." checked={prefs.marketing} onChange={(v) => setPrefs({...prefs, marketing: v})} testId="cookie-toggle-marketing" />
              <ConsentRow icon={Settings2} title="Preferences" description="Remember your theme, language, and other UI choices." checked={prefs.preferences} onChange={(v) => setPrefs({...prefs, preferences: v})} testId="cookie-toggle-preferences" />
            </div>
            <div className="p-6 border-t border-black/5 dark:border-white/10 flex justify-end gap-2">
              <button onClick={onRejectNon} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="cookie-modal-reject">Reject non-essential</button>
              <button onClick={onSave} className="btn-primary !py-2 !px-4 !text-sm" data-testid="cookie-modal-save">Save preferences</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ConsentRow({ icon: Icon, title, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-black/5 dark:border-white/10 p-4">
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#1565FF]/10 text-[#1565FF] dark:bg-white/5 dark:text-[#4FC3F7]">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-sm font-medium tracking-tight">{title}</div>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      <label className="inline-flex items-center">
        <input
          type="checkbox" checked={checked} disabled={disabled}
          data-testid={testId}
          onChange={(e) => onChange?.(e.target.checked)}
          className="peer sr-only"
        />
        <span className={`w-10 h-6 rounded-full transition-colors ${checked ? "bg-[#1565FF]" : "bg-black/20 dark:bg-white/20"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
          <span className={`block h-5 w-5 bg-white rounded-full mt-0.5 transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`}></span>
        </span>
      </label>
    </div>
  );
}
